import { useLocation } from "react-router";
import { useCallback, useEffect, useState } from "react";
import {
  DEFAULT_PRICE_RANGE,
  DEFAULT_SORTING,
  INITIAL_PAGINATION,
  INITIAL_PRODUCT_SOURCE,
  PAGE_SIZE
} from "./constants";
import { isValidNumberWithinBoundary } from "utils/numbers";
import { getProductSource, getProductSummaries } from "./requests";
import { mapPaginationResponse } from "./mappers";

export const useGetBrandDiscounts = ({ productSourceId, brandSlug }) => {
  const { search } = useLocation();
  const searchToObject = () => {
    const searchParams = new URLSearchParams(search);
    return Object.fromEntries(searchParams.entries());
  };
  const searchParams = searchToObject();
  const isValidNumber = (value) =>
    isValidNumberWithinBoundary(value, {
      min: Number(DEFAULT_PRICE_RANGE.priceMin),
      max: Number(DEFAULT_PRICE_RANGE.priceMax)
    });

  const getInitialPriceRange = () => {
    const { priceMin: paramPriceMin, priceMax: paramPriceMax } = searchParams;

    if (paramPriceMin || paramPriceMax) {
      const [min, max] = [paramPriceMin, paramPriceMax].map((range) =>
        isValidNumber(range) ? range : ""
      );

      return { priceMin: min, priceMax: max, search: true };
    }

    return DEFAULT_PRICE_RANGE;
  };

  const [sorting, setSorting] = useState(DEFAULT_SORTING);
  const [priceRange, setPriceRange] = useState(getInitialPriceRange());
  const [hasFilters, setHasFilters] = useState(false);
  const [isFetchedAndFiltered, setIsFetchedAndFiltered] = useState(false);

  const [isProductSourceLoading, setIsProductSourceLoading] = useState(true);
  const [isProductSummariesLoading, setIsProductSummariesLoading] =
    useState(true);
  const [productSource, setProductSource] = useState(INITIAL_PRODUCT_SOURCE);
  const [productSummaries, setProductSummaries] = useState([]);
  const [pagination, setPagination] = useState(INITIAL_PAGINATION);

  const fetchProductSource = useCallback(async () => {
    setIsProductSourceLoading(true);
    if (!brandSlug) return;
    try {
      const fetchedProductSource = await getProductSource({ brandSlug });

      if (fetchedProductSource) {
        setProductSource(fetchedProductSource.data);
      }
    } catch (error) {
      setProductSource(null);
    } finally {
      setIsProductSourceLoading(false);
    }
  }, [brandSlug]);

  const fetchProductSummaries = useCallback(
    async (nextPage) => {
      if (!productSourceId) return null;

      setIsProductSummariesLoading(true);

      try {
        const order = `${sorting.sort} ${sorting.order}`;

        const fetchedProductSummaries = await getProductSummaries({
          productSourceId,
          priceGreater: priceRange.priceMin || DEFAULT_PRICE_RANGE.priceMin,
          priceLower: priceRange.priceMax || DEFAULT_PRICE_RANGE.priceMax,
          page: nextPage || pagination.currentPage,
          perPage: PAGE_SIZE,
          order
        });

        if (!fetchedProductSummaries) return null;
        if (hasFilters) setIsFetchedAndFiltered(true);

        setProductSummaries(fetchedProductSummaries.data.listings);
        setPagination(mapPaginationResponse(fetchedProductSummaries.data.meta));
      } catch (error) {
        setProductSource(null);
      } finally {
        setIsProductSummariesLoading(false);
      }
    },
    [
      productSourceId,
      sorting,
      priceRange.priceMin,
      priceRange.priceMax,
      pagination.currentPage,
      hasFilters
    ]
  );

  const handlePriceRange = (props) => {
    setHasFilters(true);
    setPage(1);
    setPriceRange(props);
  };

  const setPage = (page) => {
    setPagination((state) => ({ ...state, currentPage: page }));
  };

  useEffect(() => {
    fetchProductSource();
  }, [fetchProductSource]);

  useEffect(() => {
    window.scrollTo(0, 0); // scroll to top when current_page changes
  }, [pagination.currentPage]);

  useEffect(() => {
    fetchProductSummaries();
  }, [fetchProductSummaries]);

  return {
    isLoading: isProductSummariesLoading || isProductSourceLoading,
    productSource,
    productSummaries,
    pagination,
    setPage,
    fetchProductSummaries,
    filters: {
      setSorting,
      setPriceRange: handlePriceRange,
      sorting,
      priceRange,
      hasFilters: isFetchedAndFiltered
    }
  };
};
